import React from "react"
import { makeStyles } from "@mui/styles"
import SvgIcon from "@mui/material/SvgIcon"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { COLORS } from "../../utils"
import { formatOutputWeight } from "../../utils/lotInventoryUtils"

const useStyles = makeStyles(() => ({
	total: {
		margin: "4px 0 0",
		fontSize: 16,
		lineHeight: "22px",
		color: COLORS.BLACK_500
	},
	totalValue: {
		color: COLORS.LIGHT_BLACK
	},
	destinations: {
		marginTop: 14,
		display: "flex",
		flexWrap: "wrap",
		justifyContent: "center",
		gap: 12
	},
	destination: {
		height: 32,
		boxSizing: "border-box",
		display: "inline-flex",
		alignItems: "center",
		gap: 5,
		padding: "0 12px 0 8px",
		borderRadius: 16,
		border: "1px solid",
		fontSize: 15,
		color: COLORS.LIGHT_BLACK,
		whiteSpace: "nowrap"
	},
	kitchen: {
		borderColor: COLORS.OUTPUT_KITCHEN_300,
		backgroundColor: COLORS.OUTPUT_KITCHEN_50,
		"& svg": { color: COLORS.OUTPUT_KITCHEN_900 }
	},
	pastry: {
		borderColor: COLORS.PURPLE_300,
		backgroundColor: COLORS.PURPLE_50,
		"& svg": { color: COLORS.PURPLE_800 }
	},
	icon: {
		fontSize: 22,
		fill: "none",
		stroke: "currentColor",
		strokeWidth: 1.8,
		strokeLinecap: "round",
		strokeLinejoin: "round"
	},
	destinationWeight: {
		marginLeft: 5,
		fontWeight: 500
	},
	divider: {
		// direct child of the panel, which spaces its blocks evenly: no margin of its own
		display: "block",
		width: 116,
		margin: "0 auto",
		borderTop: `1px solid ${COLORS.PRIMARY_BORDER}`
	},
	note: {
		margin: "20px auto 0",
		width: 380,
		maxWidth: "100%",
		boxSizing: "border-box",
		display: "flex",
		alignItems: "flex-start",
		gap: 12,
		padding: "12px 16px",
		borderRadius: 4,
		backgroundColor: COLORS.PRIMARY_BLUE_50,
		color: COLORS.LIGHT_BLACK,
		fontSize: 16,
		lineHeight: "24px",
		textAlign: "left"
	},
	noteIcon: {
		flexShrink: 0,
		marginTop: 1,
		color: COLORS.BLUE_700
	}
}))

const KitchenIcon = ({ className }) => (
	<SvgIcon className={className} viewBox="0 0 24 24">
		<path d="M12 3c1.918 0 3.52 1.35 3.91 3.151a4 4 0 0 1 2.09 7.723v7.126h-12v-7.126a4 4 0 1 1 2.092 -7.723a4 4 0 0 1 3.908 -3.151z" />
		<path d="M6.161 17.009l11.839 -.009" />
	</SvgIcon>
)

const PastryIcon = ({ className }) => (
	<SvgIcon className={className} viewBox="0 0 24 24">
		<path d="M8 13v.01" />
		<path d="M12 17v.01" />
		<path d="M12 12v.01" />
		<path d="M16 14v.01" />
		<path d="M11 8v.01" />
		<path d="M13.148 3.476l2.667 1.104a4 4 0 0 0 4.656 6.14l.053 .132a3 3 0 0 1 0 2.296q -.745 1.18 -1.024 1.852q -.283 .684 -.66 2.216a3 3 0 0 1 -1.624 1.623q -1.572 .394 -2.216 .661q -.712 .295 -1.852 1.024a3 3 0 0 1 -2.296 0q -1.203 -.754 -1.852 -1.024q -.707 -.292 -2.216 -.66a3 3 0 0 1 -1.623 -1.624q -.397 -1.577 -.661 -2.216q -.298 -.718 -1.024 -1.852a3 3 0 0 1 0 -2.296q .719 -1.116 1.024 -1.852q .257 -.62 .66 -2.216a3 3 0 0 1 1.624 -1.623q 1.547 -.384 2.216 -.661q .687 -.285 1.852 -1.024a3 3 0 0 1 2.296 0" />
	</SvgIcon>
)

/**
 * Gross weight still needed for the lot's supplier item (same source as the traceability voucher),
 * before rounding up to whole stock units, with its destinations: kitchen and pastry recipes.
 * Once something was output, the destinations lose their weight: an output is not tied to a destination,
 * so the remaining need cannot be split. The day's need is given in the already output note.
 */
const OutputNeedSummary = ({ outputNeed }) => {
	const classes = useStyles()

	if (!outputNeed || !outputNeed.total) return null

	const { pastry, kitchen, alreadyOutput, remaining } = outputNeed
	// weights only when they split the need shown: not for a single destination, nor once something was output
	const showWeights = pastry > 0 && kitchen > 0 && !(alreadyOutput > 0)
	const destinations = [
		{ label: "Cuisine", weight: kitchen, className: classes.kitchen, Icon: KitchenIcon },
		{ label: "Pâtisserie", weight: pastry, className: classes.pastry, Icon: PastryIcon }
	].filter((destination) => destination.weight > 0)

	// everything was output: only the already output note remains
	if (!(remaining > 0)) return null

	return (
		<>
			<p className={classes.total}>
				soit en besoin brut : <span className={classes.totalValue}>{formatOutputWeight(remaining)}</span>
			</p>
			{destinations.length > 0 &&
				<div className={classes.destinations}>
					{destinations.map(({ label, weight, className, Icon }) => (
						<span key={label} className={`${classes.destination} ${className}`}>
							<Icon className={classes.icon} />
							<span>{label}</span>
							{showWeights && <span className={classes.destinationWeight}>{formatOutputWeight(weight)}</span>}
						</span>
					))}
				</div>
			}
		</>
	)
}

/**
 * Separates the available quantity from the output section, when there is a need to show
 */
export const OutputNeedDivider = ({ outputNeed }) => {
	const classes = useStyles()

	if (!outputNeed || !outputNeed.total) return null

	return <span className={classes.divider} />
}

/**
 * Output already done for the production day, in stock units and kg
 */
export const AlreadyOutputNote = ({ outputNeed, stockUnit }) => {
	const classes = useStyles()

	if (!outputNeed || !outputNeed.alreadyOutput) return null

	const { total, alreadyOutput, alreadyOutputUnits } = outputNeed
	// stock managed by weight (unity 0 = kg): the number of units adds nothing to the weight
	const isWeightUnit = stockUnit?.unity?.unity === 0
	const pieces = `${alreadyOutputUnits} pièce${alreadyOutputUnits > 1 ? "s" : ""}`
	const quantity = isWeightUnit ? formatOutputWeight(alreadyOutput) : `${pieces} (${formatOutputWeight(alreadyOutput)})`

	return (
		<div role="note" className={classes.note}>
			<InfoOutlinedIcon className={classes.noteIcon} />
			<span>Déjà sorti pour aujourd&apos;hui : {quantity} sur {formatOutputWeight(total)}.</span>
		</div>
	)
}

export default OutputNeedSummary
